import React, { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { loadSeller } from "../../redux/actions/user";
import { HiOutlineArrowLeft, HiOutlineShieldCheck, HiOutlineTruck, HiOutlineCheckCircle } from "react-icons/hi";

const ShopLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${server}/shop/login-shop`, { email, password }, { withCredentials: true });
      toast.success("Login successful!");
      try {
        await dispatch(loadSeller());
        navigate("/dashboard");
      } catch {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Login failed";
      if (msg.includes("Only users with Supplier role") || msg.includes("Please use regular user login")) {
        toast.error("Please use the regular user login.");
        navigate("/login");
      } else if (msg.includes("Your role has been changed")) {
        toast.error("Your role has been changed. Please use the regular user login.");
        navigate("/login");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-between p-14">
        <Link to="/">
          <img src="/logo (10).png" alt="Logo" className="h-10 w-auto invert brightness-200 object-contain" />
        </Link>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-accent-500 mb-4">Vendor Portal</p>
          <h1 className="text-4xl font-bold text-white leading-snug mb-6">
            Your vendor<br />dashboard awaits.
          </h1>
          <p className="text-secondary-400 text-sm leading-relaxed max-w-sm mb-10">
            Manage products, track orders, and grow your wellness business on India's trusted marketplace.
          </p>
          <div className="space-y-3">
            {[
              { icon: HiOutlineShieldCheck, text: "Secure vendor login" },
              { icon: HiOutlineTruck, text: "Manage orders & shipments" },
              { icon: HiOutlineCheckCircle, text: "Track payments & earnings" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <item.icon className="w-4 h-4 text-accent-500 flex-shrink-0" />
                <span className="text-secondary-400 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-secondary-600 text-xs">© {new Date().getFullYear()} Positive Moments</p>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 relative">
        <button onClick={() => navigate("/")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-secondary-500 hover:text-black transition-colors">
          <HiOutlineArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo (10).png" alt="Logo" className="h-10 w-auto object-contain" />
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-accent-600 mb-2">Vendor Portal</p>
          <h2 className="text-3xl font-bold text-black mb-2">Vendor Sign In</h2>
          <p className="text-secondary-500 text-sm mb-8">
            Don't have a vendor account?{" "}
            <Link to="/shop-create" className="text-black font-semibold underline underline-offset-2 hover:text-accent-600 transition-colors">
              Apply now
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-secondary-600 mb-2">Email</label>
              <input
                type="email" required placeholder="you@example.com" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-secondary-600">Password</label>
                <Link to="/shop-forgot-password" className="text-xs text-secondary-400 hover:text-black transition-colors">Forgot?</Link>
              </div>
              <div className="relative">
                <input
                  type={visible ? "text" : "password"} required placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-secondary-200 text-black placeholder-secondary-400 text-sm focus:outline-none focus:border-black transition-colors bg-white"
                />
                <button type="button" onClick={() => setVisible(!visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-black transition-colors">
                  {visible ? <AiOutlineEye size={18} /> : <AiOutlineEyeInvisible size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3.5 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors disabled:opacity-50 mt-2">
              {loading ? "Signing in" : "Sign In"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-secondary-100 space-y-3">
            <p className="text-xs text-secondary-400 text-center">
              Not a vendor yet?{" "}
              <Link to="/shop-create" className="text-black font-medium hover:text-accent-600 transition-colors">
                Register as Vendor
              </Link>
            </p>
            <p className="text-xs text-secondary-400 text-center">
              Customer account?{" "}
              <Link to="/login" className="text-black font-medium hover:text-accent-600 transition-colors">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopLogin;
